import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Trash2, User as UserIcon } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { AccessGroupForm } from "@/components/admin/AccessGroupForm"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Autocomplete } from "@/components/ui/autocomplete"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useUserSearch } from "@/hooks/use-user-search"
import type { AccessApp, AccessGroupMember, AccessGroupRow, LevelOption } from "@/types/access"

export default function AdminAccessGroupsEdit({
  group,
  members,
  apps,
  levels,
}: {
  group: AccessGroupRow
  members: AccessGroupMember[]
  apps: AccessApp[]
  levels: LevelOption[]
}) {
  const description = `The ${group.name} access group: its level on each app, and who is in it.`

  return (
    <>
      <Head title={group.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={group.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <Link
          href="/admin/access_groups"
          className="mb-4 inline-flex items-center gap-1.5 text-sm no-underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to groups
        </Link>

        <PageHeader
          title={group.name}
          description={group.description ?? undefined}
          actions={
            group.joined_at_first_sign_in ? <Badge tone="signal">Joins new accounts</Badge> : null
          }
        />

        <div className="mt-6">
          <FlashNotice />
        </div>

        <AccessGroupForm group={group} apps={apps} levels={levels} submitLabel="Save changes" />

        <Members group={group} members={members} />
        <DeleteSection group={group} />
      </AdminShell>
    </>
  )
}

function Members({ group, members }: { group: AccessGroupRow; members: AccessGroupMember[] }) {
  const [query, setQuery] = React.useState("")
  const { suggestions, loading } = useUserSearch(query)
  const memberIds = new Set(members.map((member) => member.user_id))

  const add = (userId: number) => {
    router.post(
      `/admin/access_groups/${group.id}/members`,
      { user_id: userId },
      { preserveScroll: true, onSuccess: () => setQuery("") },
    )
  }

  const remove = (userId: number) => {
    router.delete(`/admin/access_groups/${group.id}/members/${userId}`, { preserveScroll: true })
  }

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>People in this group</h2>
      <p className="mt-1 text-sm text-ink-muted">
        {members.length === 0
          ? "Nobody yet."
          : `${members.length} ${members.length === 1 ? "person" : "people"}. A change here takes effect on their next click.`}
      </p>

      <div className="mt-4 max-w-md">
        <label htmlFor="member-search">Add a person</label>
        <Autocomplete
          id="member-search"
          className="mt-1"
          value={query}
          onValueChange={setQuery}
          suggestions={suggestions.map((person) => ({
            id: person.id,
            label: memberIds.has(person.id) ? `${person.name} (already in)` : person.name,
            hint: [person.email, person.organization].filter(Boolean).join(" · ") || null,
          }))}
          loading={loading}
          onSelect={(suggestion) => add(Number(suggestion.id))}
          placeholder="Name, email, username or organization"
          hintMessage="Type a name, an email, a username or an organization."
          emptyMessage="No accounts match."
          aria-label="Find a person to add"
        />
      </div>

      {members.length > 0 && (
        <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
          {members.map((member) => (
            <li key={member.user_id} className="flex items-center gap-3 px-4 py-3">
              <UserIcon className="h-4 w-4 shrink-0 text-ink-muted" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    href={`/admin/users/${member.user_id}`}
                    className="truncate text-sm font-medium no-underline"
                  >
                    {member.name}
                  </Link>
                  {member.admin && <Badge tone="accent">Admin</Badge>}
                  {member.placeholder && <Badge tone="muted">Never signed in</Badge>}
                </div>
                <div className="truncate text-xs text-ink-muted">
                  {[member.email, member.organization].filter(Boolean).join(" · ") || "no email"}
                  {" · "}
                  {member.added_by
                    ? `added by ${member.added_by} ${formatDate(member.added_at)}`
                    : `added automatically ${formatDate(member.added_at)}`}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(member.user_id)}
                aria-label={`Remove ${member.name} from ${group.name}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function DeleteSection({ group }: { group: AccessGroupRow }) {
  const consequence =
    group.member_count === 0
      ? "Nobody is in it, so nobody's access changes."
      : `${
          group.member_count === 1
            ? "The one person in it loses"
            : `The ${group.member_count} people in it lose`
        } what it gives and falls back to their other groups and overrides. Anyone in no other group has No Access to every app.`

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>Delete this group</h2>
      <p className="mt-1">{consequence}</p>
      {group.joined_at_first_sign_in && (
        <p className="mt-2 text-sm text-ink-muted">
          Brand-new accounts are placed in this group. Once it is gone they join no
          group at first sign-in unless another group is set to take them.
        </p>
      )}

      <Dialog>
        <DialogTrigger asChild>
          <Button variant="danger" className="mt-4">
            <Trash2 className="h-4 w-4" />
            Delete…
          </Button>
        </DialogTrigger>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete {group.name}?</DialogTitle>
            <DialogDescription>{consequence} This can&rsquo;t be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">Keep it</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                variant="danger"
                onClick={() => router.delete(`/admin/access_groups/${group.id}`)}
              >
                Delete group
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
